import React, { useContext, useEffect, useState } from "react";
import { faUserSecret } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RouteComponentProps } from "react-router-dom";
import { Routes } from "../../../classes/Routes";
import Alert from "../../../components/UI/Alert/Alert";
import Button from "../../../components/UI/Button/Button";
import ConfirmDialog from "../../../components/UI/ConfirmDialog/ConfirmDialog";
import Container from "../../../components/UI/Container/Container";
import  {
  EInputType,
  IInputField,
} from "../../../components/UI/Input/Input";
import { useInputs } from "../../../components/UI/Input/useInputs";
import InputGroup from "../../../components/UI/InputGroup/InputGroup";
import ModalContext, {
  defaultModal,
  EModalSize,
} from "../../../components/UI/Modal/ModalContext";
import Spinner from "../../../components/UI/Spinner/Spinner";
import { EInputUpdateAction } from "../../../context/InputContext";
import { IAppState, IOrganization, IOrganizationState } from "../../../interfaces";
import { countries } from "../../../shared/countries";
import {
  
  initForm,
} from "../../../shared/utility";
import * as actions from "../../../store/actions";
import { ETranslation } from "../../../translations/translation-keys";
import Users from "../../Users/Users";

interface IMatch {
  id: string;
}

interface IProps extends RouteComponentProps<IMatch> {}

export enum EOperator {
  name = "name",
  businessId = "businessId",
  streetAddress = "streetAddress",
  zip = "zip",
  city = "city",
  countryCode = "countryCode",
  operatorToken = "operatorToken",
}

const Operator: React.FC<IProps> = ({ match, history, location }) => {
  const dispatch = useDispatch();

  const { setModal } = useContext(ModalContext);

  const authUser = useSelector((state: IAppState) => state.auth.authUser);
  const { loading, error, organization, saveOrUpdateOk } = useSelector<IAppState, IOrganizationState>(state => state.organization);

  const { t } = useTranslation();
  const { id } = match.params;

  const [inputs, setInputs] = useState<IInputField>({
    [EOperator.name]: {
      type: EInputType.text,
      labelTranslation: ETranslation.OPERATOR_NAME,
      placeholderTranslation: ETranslation.OPERATOR_NAME,
      value: "",
      validation: {
        required: true,
      },
    },
    [EOperator.businessId]: {
      type: EInputType.text,
      labelTranslation: ETranslation.OPERATOR_BUSINESS_ID,
      placeholderTranslation: ETranslation.OPERATOR_BUSINESS_ID,
      value: "",
    },
    [EOperator.streetAddress]: {
      type: EInputType.text,
      labelTranslation: ETranslation.COMMON_STREET_ADDRESS,
      placeholderTranslation: ETranslation.COMMON_STREET_ADDRESS,
      value: "",
    },
    [EOperator.zip]: {
      type: EInputType.text,
      labelTranslation: ETranslation.COMMON_ZIP,
      placeholderTranslation: ETranslation.COMMON_ZIP,
      value: "",
    },
    [EOperator.city]: {
      type: EInputType.text,
      labelTranslation: ETranslation.COMMON_CITY,
      placeholderTranslation: ETranslation.COMMON_CITY,
      value: "",
    },
    [EOperator.countryCode]: {
      type: EInputType.reactSelect,
      labelTranslation: ETranslation.COMMON_COUNTRY,
      placeholderTranslation: ETranslation.COMMON_COUNTRY,
      value: countries[0].value,
      options: countries,
    },
    [EOperator.operatorToken]: {
      type: EInputType.static,
      labelTranslation: ETranslation.COMMON_TOKEN,
      value: "",
    },
  });


  useEffect(() => {
    if (saveOrUpdateOk) {
      history.push(Routes.OPERATORS);
    }
    return () => {
      dispatch(actions.clearOrganization());
    }
  }, [saveOrUpdateOk, dispatch, history]);

  useEffect(() => {
    if (Routes.isNotNew(id)) {
      dispatch(actions.getOrganization(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (organization) {
      initForm(setInputs, organization);
    }
  }, [organization]);

  const { createInput, submit } = useInputs({
    inputs,
    setInputs,
    options: {
      updateAction: EInputUpdateAction.OPERATOR
    }
  });

  const submitHandler = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.preventDefault();
    const organization = await submit() as IOrganization;
    if (!organization) return;

    if (Routes.isNew(id)) {
      dispatch(actions.saveOrganization(organization));
    } else {
      organization.id = id;
      dispatch(actions.updateOrganization(organization));
    }
  };

  const deleteOperator = (id: string) => {
    dispatch(actions.deleteServiceProvider(id));
    history.goBack();
    setModal(defaultModal);
  };

  const setConfirmDeleteDialog = () =>
    setModal({
      isOpen: true,
      title: t(ETranslation.COMMON_REMOVE),
      size: EModalSize.SMALL,
      content: (
        <ConfirmDialog
          body={<p>{t(ETranslation.CONFIRM_DELETE_OPERATOR)}</p>}
          onConfirm={() => deleteOperator(id)}
          onCancel={() => setModal(defaultModal)}
        />
      ),
    });

  const handleImpersonateOperator = () => {
    dispatch(
      actions.startImpersonation({
        orgId: +id,
        orgName:
          organization?.name ||
          (inputs[EOperator.name]?.value as string) ||
          `Operaattori ${id}`,
        level: "OPERATOR",
        allowWrite: false,
      })
    );
    history.push("/");
  };

  return (
    <Container>
      {error && <Alert children={error} />}
      <h2>
        {Routes.isNew(id)
          ? t(ETranslation.TITLE_OPERATOR_NEW)
          : t(ETranslation.TITLE_OPERATOR)}
      </h2>
      {loading ? (
        <Spinner />
      ) : (
        <>
          {createInput(EOperator.name)}
          {createInput(EOperator.businessId)}
          {createInput(EOperator.streetAddress)}
          <InputGroup>
            {createInput(EOperator.zip)}
            {createInput(EOperator.city)}
          </InputGroup>
          {createInput(EOperator.countryCode)}
          {Routes.isNotNew(id) && createInput(EOperator.operatorToken)}
        </>
      )}
      <InputGroup>
        <Button disabled={loading} onClick={submitHandler}>
          {t(ETranslation.COMMON_SAVE)}
        </Button>
        <Button onClick={() => history.goBack()}>
          {t(ETranslation.COMMON_RETURN)}
        </Button>
        {Routes.isNotNew(id) && (
          <Button disabled={loading} onClick={setConfirmDeleteDialog}>
            {t(ETranslation.COMMON_REMOVE)}
          </Button>
        )}
        {authUser?.isPrimary && Routes.isNotNew(id) && (
          <Button
            onClick={handleImpersonateOperator}
            icon={faUserSecret}
            style={{ backgroundColor: "#f59e0b", color: "#fff", borderColor: "#d97706" }}
          >
            {t(ETranslation.IMPERSONATE_ACTION_OPERATOR)}
          </Button>
        )}
      </InputGroup>
      {Routes.isNotNew(id) && (
        <Users
          organizationId={id}
          history={history}
          location={location}
          match={match}
        />
      )}
    </Container>
  );
};

export default Operator;
